
import { supabase } from "@/integrations/supabase/client";
import { ApiTokens, SaveApiTokensData, DatabaseTokenRecord } from "./types";
import { getDefaultTokens } from "./utils";

export class ApiTokensService {
  static async fetchTokens(brandId: string): Promise<ApiTokens> {
    try {
      const { data, error } = await supabase
        .from('api_tokens')
        .select('*')
        .eq('brand_id', brandId)
        .maybeSingle();

      if (error) {
        console.error("ApiTokensService: Database error:", error);
        throw new Error(error.message);
      }

      if (!data) {
        return getDefaultTokens();
      }

      
      
      // Cast DB record to any to safely access newly-added columns without strict DB type definitions
      const db: any = data;
      // Transform database record to ApiTokens format
      const tokens: ApiTokens = {
        openai: db.openai || '',
        elevenlabs: db.elevenlabs || '',
        gemini: db.gemini || '',
        prompts_per_combination: data.prompts_per_combination || 5,
  openai_verified: db.openai_verified || false,
  elevenlabs_verified: db.elevenlabs_verified || false,
  gemini_verified: db.gemini_verified || false,
  openai_verified_at: db.openai_verified_at,
  elevenlabs_verified_at: db.elevenlabs_verified_at,
  gemini_verified_at: db.gemini_verified_at,
        dataforseo_login: data.dataforseo_login || '',
        dataforseo_password: data.dataforseo_password || '',
        dataforseo_verified: data.dataforseo_verified || false,
        dataforseo_verified_at: data.dataforseo_verified_at,
        openai_model: data.openai_model || 'gpt-4o-mini',
        gemini_model: data.gemini_model || 'gemini-2.5-flash-lite',
        firecrawl: data.firecrawl || '',
        firecrawl_verified: data.firecrawl_verified || false,
        firecrawl_verified_at: data.firecrawl_verified_at,
        perplexity: data.perplexity || '',
        perplexity_verified: data.perplexity_verified || false,
        perplexity_verified_at: data.perplexity_verified_at,
        perplexity_model: this.migratePerplexityModel(data.perplexity_model),
        apify: data.apify || '',
        apify_verified: data.apify_verified || false,
        apify_verified_at: data.apify_verified_at,
        youtube_api_key: data.youtube_api_key || '',
        youtube_verified: data.youtube_verified || false,
        youtube_verified_at: data.youtube_verified_at,
        claude: data.claude || '',
        claude_verified: data.claude_verified || false,
        claude_verified_at: data.claude_verified_at,
        claude_model: this.migrateClaudeModel(data.claude_model),
        hubspot: data.hubspot || '',
        hubspot_verified: data.hubspot_verified || false,
        hubspot_verified_at: data.hubspot_verified_at,
        fal_ai: data.fal_ai || '',
        fal_ai_verified: data.fal_ai_verified || false,
        fal_ai_verified_at: data.fal_ai_verified_at,
        hailuoai: data.hailuoai || '',
        hailuoai_group_id: db.hailuoai_group_id || '',
        hailuoai_verified: data.hailuoai_verified || false,
        hailuoai_verified_at: data.hailuoai_verified_at,
        lumalabs: data.lumalabs || '',
        lumalabs_verified: data.lumalabs_verified || false,
        lumalabs_verified_at: data.lumalabs_verified_at,
        lumalabs_model: data.lumalabs_model || 'ray-flash-2',
        reddit_client_id: db.reddit_client_id || '',
        reddit_client_secret: db.reddit_client_secret || '',
        reddit_user_agent: db.reddit_user_agent || '',
        reddit_verified: db.reddit_verified || false,
        reddit_verified_at: db.reddit_verified_at,
        byteplus: db.byteplus || '',
        byteplus_verified: db.byteplus_verified || false,
        byteplus_verified_at: db.byteplus_verified_at,
        byteplus_model: db.byteplus_model || 'seedreal-4.0',
        wordpress_url: db.wordpress_url || '',
        wordpress_username: db.wordpress_username || '',
        wordpress_password: db.wordpress_password || '',
        wordpress_verified: db.wordpress_verified || false,
        wordpress_verified_at: db.wordpress_verified_at,
        apiflash: db.apiflash || '',
        apiflash_verified: db.apiflash_verified || false,
        apiflash_verified_at: db.apiflash_verified_at,
        heygen: db.heygen || '',
        heygen_verified: db.heygen_verified || false,
        heygen_verified_at: db.heygen_verified_at,
        youtube_access_token: db.youtube_access_token || '',
        youtube_refresh_token: db.youtube_refresh_token || '',
        youtube_token_expires_at: db.youtube_token_expires_at || '',
        google_calendar_access_token: db.google_calendar_access_token || '',
        google_calendar_refresh_token: db.google_calendar_refresh_token || '',
        google_calendar_token_expires_at: db.google_calendar_token_expires_at || '',
        google_calendar_email: db.google_calendar_email || '',
        google_calendar_client_id: db.google_calendar_client_id || '',
        google_calendar_client_secret: db.google_calendar_client_secret || '',
        cartesia: db.cartesia || '',
        cartesia_verified: db.cartesia_verified || false,
        cartesia_verified_at: db.cartesia_verified_at,
        cartesia_model: db.cartesia_model || 'sonic-english',
        gamma: db.gamma || '',
        gamma_verified: db.gamma_verified || false,
        gamma_verified_at: db.gamma_verified_at,
        pagespeed: db.pagespeed || '',
        pagespeed_verified: db.pagespeed_verified || false,
        pagespeed_verified_at: db.pagespeed_verified_at,
        ghl_api_key: db.ghl_api_key || '',
        ghl_location_id: db.ghl_location_id || '',
        ghl_verified: db.ghl_verified || false,
        ghl_verified_at: db.ghl_verified_at,
        slack_bot_token: db.slack_bot_token || '',
        slack_default_channel: db.slack_default_channel || '',
        slack_verified: db.slack_verified || false,
        slack_verified_at: db.slack_verified_at,
        google_sheets_service_account_json: db.google_sheets_service_account_json || '',
        google_sheets_default_spreadsheet_id: db.google_sheets_default_spreadsheet_id || '',
        google_sheets_access_token: db.google_sheets_access_token || '',
        google_sheets_refresh_token: db.google_sheets_refresh_token || '',
        google_sheets_token_expires_at: db.google_sheets_token_expires_at || '',
        google_sheets_email: db.google_sheets_email || '',
        xero_client_id: db.xero_client_id || '',
        xero_client_secret: db.xero_client_secret || '',
        xero_verified: db.xero_verified || false,
        xero_verified_at: db.xero_verified_at,
        domainr: db.domainr || '',
        domainr_verified: db.domainr_verified || false,
        domainr_verified_at: db.domainr_verified_at,
        vimeo_access_token: db.vimeo_access_token || '',
        vimeo_verified: db.vimeo_verified || false,
        vimeo_verified_at: db.vimeo_verified_at,
        youtube_client_id: db.youtube_client_id || '',
        youtube_client_secret: db.youtube_client_secret || '',
      };

      return tokens;
    } catch (error: any) {
      console.error("ApiTokensService: Fetch error:", error);
      throw error;
    }
  }

  static async saveTokens(tokens: SaveApiTokensData, brandId: string, userId: string): Promise<void> {
    try {
      // First check if a record exists for this brand (regardless of user)
      const { data: existingRecord } = await supabase
        .from('api_tokens')
        .select('id')
        .eq('brand_id', brandId)
        .maybeSingle();

      // Sanitize timestamp fields - convert empty strings to null
      const sanitizeTimestamp = (value: any): string | null | undefined => {
        if (value === '' || (typeof value === 'string' && value.trim() === '')) return null;
        return value;
      };

      const tokenData = {
        user_id: userId,
        brand_id: brandId,
  openai: tokens.openai,
  elevenlabs: tokens.elevenlabs,
  gemini: tokens.gemini,
        prompts_per_combination: tokens.prompts_per_combination,
        openai_verified: tokens.openai_verified,
  elevenlabs_verified: tokens.elevenlabs_verified,
        gemini_verified: tokens.gemini_verified,
        openai_verified_at: sanitizeTimestamp(tokens.openai_verified_at),
  elevenlabs_verified_at: sanitizeTimestamp(tokens.elevenlabs_verified_at),
        gemini_verified_at: sanitizeTimestamp(tokens.gemini_verified_at),
        dataforseo_login: tokens.dataforseo_login,
        dataforseo_password: tokens.dataforseo_password,
        dataforseo_verified: tokens.dataforseo_verified,
        dataforseo_verified_at: sanitizeTimestamp(tokens.dataforseo_verified_at),
        openai_model: tokens.openai_model,
        gemini_model: (tokens as any).gemini_model,
        firecrawl: tokens.firecrawl,
        firecrawl_verified: tokens.firecrawl_verified,
        firecrawl_verified_at: sanitizeTimestamp(tokens.firecrawl_verified_at),
        perplexity: tokens.perplexity,
        perplexity_verified: tokens.perplexity_verified,
        perplexity_verified_at: sanitizeTimestamp(tokens.perplexity_verified_at),
        perplexity_model: tokens.perplexity_model,
        apify: tokens.apify,
        apify_verified: tokens.apify_verified,
        apify_verified_at: sanitizeTimestamp(tokens.apify_verified_at),
        youtube_api_key: tokens.youtube_api_key,
        youtube_verified: tokens.youtube_verified,
        youtube_verified_at: sanitizeTimestamp(tokens.youtube_verified_at),
        claude: tokens.claude,
        claude_verified: tokens.claude_verified,
        claude_verified_at: sanitizeTimestamp(tokens.claude_verified_at),
        claude_model: tokens.claude_model,
        hubspot: tokens.hubspot,
        hubspot_verified: tokens.hubspot_verified,
        hubspot_verified_at: sanitizeTimestamp(tokens.hubspot_verified_at),
        fal_ai: tokens.fal_ai,
        fal_ai_verified: tokens.fal_ai_verified,
        fal_ai_verified_at: sanitizeTimestamp(tokens.fal_ai_verified_at),
        hailuoai: tokens.hailuoai,
        hailuoai_group_id: tokens.hailuoai_group_id,
        hailuoai_verified: tokens.hailuoai_verified,
        hailuoai_verified_at: sanitizeTimestamp(tokens.hailuoai_verified_at),
        lumalabs: tokens.lumalabs,
        lumalabs_verified: tokens.lumalabs_verified,
        lumalabs_verified_at: sanitizeTimestamp(tokens.lumalabs_verified_at),
        lumalabs_model: tokens.lumalabs_model,
        reddit_client_id: tokens.reddit_client_id,
        reddit_client_secret: tokens.reddit_client_secret,
        reddit_user_agent: tokens.reddit_user_agent,
        reddit_verified: tokens.reddit_verified,
        reddit_verified_at: sanitizeTimestamp(tokens.reddit_verified_at),
        byteplus: tokens.byteplus,
        byteplus_verified: tokens.byteplus_verified,
        byteplus_verified_at: sanitizeTimestamp(tokens.byteplus_verified_at),
        byteplus_model: tokens.byteplus_model,
        wordpress_url: tokens.wordpress_url,
        wordpress_username: tokens.wordpress_username,
        wordpress_password: tokens.wordpress_password,
        wordpress_verified: tokens.wordpress_verified,
        wordpress_verified_at: sanitizeTimestamp(tokens.wordpress_verified_at),
        apiflash: tokens.apiflash,
        apiflash_verified: tokens.apiflash_verified,
        apiflash_verified_at: sanitizeTimestamp(tokens.apiflash_verified_at),
        heygen: tokens.heygen,
        heygen_verified: tokens.heygen_verified,
        heygen_verified_at: sanitizeTimestamp(tokens.heygen_verified_at),
        youtube_access_token: tokens.youtube_access_token,
        youtube_refresh_token: tokens.youtube_refresh_token,
        youtube_token_expires_at: sanitizeTimestamp(tokens.youtube_token_expires_at),
        google_calendar_access_token: tokens.google_calendar_access_token,
        google_calendar_refresh_token: tokens.google_calendar_refresh_token,
        google_calendar_token_expires_at: sanitizeTimestamp(tokens.google_calendar_token_expires_at),
        google_calendar_email: tokens.google_calendar_email,
        google_calendar_client_id: (tokens as any).google_calendar_client_id ?? null,
        google_calendar_client_secret: (tokens as any).google_calendar_client_secret ?? null,
        cartesia: tokens.cartesia,
        cartesia_verified: tokens.cartesia_verified,
        cartesia_verified_at: sanitizeTimestamp(tokens.cartesia_verified_at),
        cartesia_model: tokens.cartesia_model,
        gamma: tokens.gamma,
        gamma_verified: tokens.gamma_verified,
        gamma_verified_at: sanitizeTimestamp(tokens.gamma_verified_at),
        pagespeed: tokens.pagespeed,
        pagespeed_verified: tokens.pagespeed_verified,
        pagespeed_verified_at: sanitizeTimestamp(tokens.pagespeed_verified_at),
        ghl_api_key: tokens.ghl_api_key,
        ghl_location_id: tokens.ghl_location_id,
        ghl_verified: tokens.ghl_verified,
        ghl_verified_at: sanitizeTimestamp(tokens.ghl_verified_at),
        slack_bot_token: tokens.slack_bot_token,
        slack_default_channel: tokens.slack_default_channel,
        slack_verified: tokens.slack_verified,
        slack_verified_at: sanitizeTimestamp(tokens.slack_verified_at),
        google_sheets_service_account_json: tokens.google_sheets_service_account_json ?? null,
        google_sheets_default_spreadsheet_id: tokens.google_sheets_default_spreadsheet_id ?? null,
        google_sheets_access_token: tokens.google_sheets_access_token ?? null,
        google_sheets_refresh_token: tokens.google_sheets_refresh_token ?? null,
        google_sheets_token_expires_at: sanitizeTimestamp(tokens.google_sheets_token_expires_at),
        google_sheets_email: tokens.google_sheets_email ?? null,
        xero_client_id: tokens.xero_client_id,
        xero_client_secret: tokens.xero_client_secret,
        xero_verified: tokens.xero_verified,
        xero_verified_at: sanitizeTimestamp(tokens.xero_verified_at),
        domainr: tokens.domainr,
        domainr_verified: tokens.domainr_verified,
        domainr_verified_at: sanitizeTimestamp(tokens.domainr_verified_at),
        vimeo_access_token: (tokens as any).vimeo_access_token ?? null,
        vimeo_verified: (tokens as any).vimeo_verified ?? false,
        vimeo_verified_at: sanitizeTimestamp((tokens as any).vimeo_verified_at),
        youtube_client_id: (tokens as any).youtube_client_id ?? null,
        youtube_client_secret: (tokens as any).youtube_client_secret ?? null,
      };

      let error;

      if (existingRecord) {
        // Update existing record
        const result = await supabase
          .from('api_tokens')
          .update(tokenData)
          .eq('id', existingRecord.id);
        error = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('api_tokens')
          .insert(tokenData);
        error = result.error;
      }

      if (error) {
        console.error("ApiTokensService: Save error:", error);
        throw new Error(error.message);
      }
    } catch (error: any) {
      console.error("ApiTokensService: Save error:", error);
      throw error;
    }
  }

  static async updateVerification(
    type: 'openai' | 'gemini' | 'elevenlabs' | 'dataforseo' | 'firecrawl' | 'perplexity' | 'apify' | 'youtube' | 'claude' | 'fal_ai' | 'hailuoai' | 'lumalabs' | 'reddit' | 'byteplus' | 'wordpress' | 'apiflash' | 'heygen' | 'cartesia' | 'gamma' | 'pagespeed' | 'ghl' | 'slack' | 'xero' | 'domainr' | 'vimeo',
    isVerified: boolean,
    brandId: string,
    userId: string
  ): Promise<void> {
    try {
      const updates: any = {
        [`${type}_verified`]: isVerified,
        [`${type}_verified_at`]: isVerified ? new Date().toISOString() : null
      };

      const { error } = await supabase
        .from('api_tokens')
        .update(updates)
        .eq('brand_id', brandId);

      if (error) {
        console.error(`ApiTokensService: Error updating ${type} verification:`, error);
        throw new Error(error.message);
      }
    } catch (error: any) {
      console.error(`ApiTokensService: Update verification error:`, error);
      throw error;
    }
  }

  // Migrate old Perplexity model names to current API model names
  private static migratePerplexityModel(oldModel: string | undefined): string {
    if (!oldModel) return 'sonar';
    
    // Map old model names to current ones from official Perplexity API docs
    const modelMigrationMap: Record<string, string> = {
      'llama-3.1-sonar-small-128k-online': 'sonar',
      'llama-3.1-sonar-large-128k-online': 'sonar-pro', 
      'llama-3.1-sonar-huge-128k-online': 'sonar-reasoning',
      'sonar-small-online': 'sonar',
      'sonar-large-online': 'sonar-pro',
      'sonar-huge-online': 'sonar-reasoning',
    };

    return modelMigrationMap[oldModel] || oldModel;
  }

  // Migrate old Claude model names to current API model names
  private static migrateClaudeModel(oldModel: string | undefined): string {
    if (!oldModel) return 'claude-3-5-haiku-20241022';
    
    // Map old model names to current ones
    const modelMigrationMap: Record<string, string> = {
      'claude-4-sonnet': 'claude-sonnet-4-20250514',
      'claude-4-opus': 'claude-opus-4-20250514', 
      'claude-3-5-haiku': 'claude-3-5-haiku-20241022',
    };

    return modelMigrationMap[oldModel] || oldModel;
  }
}
